import React, { useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import Input from '../common/Input';
import Button from '../common/Button';
import { SUPPORT_CATEGORIES } from '../../utils/constants';

const TITLE_MAX = 50;
const CONTENT_MAX = 1000;

const InquiryForm = ({ onSubmit, initialData = null, submitting = false }) => {
  const [formData, setFormData] = useState({
    category: initialData?.category || '',
    title: initialData?.title || '',
    content: initialData?.content || '',
  });
  const [files, setFiles] = useState([]); // mock 첨부파일 메타
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    let next = value;
    if (name === 'title' && value.length > TITLE_MAX) next = value.slice(0, TITLE_MAX);
    if (name === 'content' && value.length > CONTENT_MAX)
      next = value.slice(0, CONTENT_MAX);

    setFormData((prev) => ({ ...prev, [name]: next }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []).map((f) => ({
      name: f.name,
      size: f.size,
    }));
    // mock: 실제 업로드 없이 메타데이터만 보관
    setFiles((prev) => [...prev, ...selected].slice(0, 3));
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.category) {
      newErrors.category = '문의 유형을 선택해주세요.';
    }
    if (!formData.title.trim()) {
      newErrors.title = '제목을 입력해주세요.';
    }
    if (!formData.content.trim()) {
      newErrors.content = '내용을 입력해주세요.';
    } else if (formData.content.trim().length < 10) {
      newErrors.content = '내용은 최소 10자 이상 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return; // 중복 제출 방지
    if (validate()) {
      onSubmit({ ...formData, attachments: files });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div>
        <label
          htmlFor="category"
          className="block text-sm font-medium text-primary mb-1"
        >
          문의 유형 <span className="text-red-600">*</span>
        </label>
        <select
          id="category"
          name="category"
          value={formData.category}
          onChange={handleChange}
          aria-invalid={!!errors.category}
          className={`w-full px-4 py-2 rounded-lg border bg-white text-primary focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${
            errors.category
              ? 'border-red-500 focus:ring-red-500'
              : 'border-accent-300 focus:ring-primary-500'
          }`}
        >
          <option value="" disabled>
            선택해주세요
          </option>
          {SUPPORT_CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
        {errors.category && (
          <p className="mt-1 text-sm text-red-600">{errors.category}</p>
        )}
      </div>

      <div>
        <Input
          label="제목"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="문의 제목을 입력해주세요"
          error={errors.title}
          required
        />
        <p className="mt-1 text-right text-xs text-accent-500">
          {formData.title.length}/{TITLE_MAX}
        </p>
      </div>

      <div>
        <label
          htmlFor="content"
          className="block text-sm font-medium text-primary mb-1"
        >
          내용 <span className="text-red-600 ml-1">*</span>
        </label>
        <textarea
          id="content"
          name="content"
          value={formData.content}
          onChange={handleChange}
          placeholder="문의 내용을 자세히 입력해주세요 (최소 10자)"
          rows={6}
          maxLength={CONTENT_MAX}
          aria-invalid={!!errors.content}
          className={`w-full px-4 py-2 rounded-lg border ${
            errors.content
              ? 'border-red-500 focus:ring-red-500'
              : 'border-accent-300 focus:ring-primary-500'
          } bg-white text-primary placeholder-accent-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 resize-none`}
        />
        <div className="mt-1 flex items-center justify-between">
          {errors.content ? (
            <p className="text-sm text-red-600">{errors.content}</p>
          ) : (
            <span />
          )}
          <span className="text-xs text-accent-500">
            {formData.content.length}/{CONTENT_MAX}
          </span>
        </div>
      </div>

      {/* 파일 첨부 (mock UI) */}
      <div>
        <span className="block text-sm font-medium text-primary mb-1">
          파일 첨부 <span className="text-xs text-accent-500">(최대 3개)</span>
        </span>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-accent-300 px-4 py-2 text-sm text-accent-700 hover:bg-accent-50">
          <Paperclip size={16} />
          파일 선택
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            className="hidden"
            accept="image/*,.pdf"
          />
        </label>
        {files.length > 0 && (
          <ul className="mt-2 space-y-1">
            {files.map((file, index) => (
              <li
                key={index}
                className="flex items-center justify-between rounded-md bg-accent-50 px-3 py-1.5 text-xs text-accent-700"
              >
                <span className="truncate">{file.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  aria-label={`${file.name} 첨부 제거`}
                  className="ml-2 text-accent-400 hover:text-red-600"
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-1 text-xs text-accent-400">
          결제 오류 화면 등을 첨부하시면 더 빠른 답변이 가능합니다. (데모 환경에서는
          실제 업로드되지 않습니다)
        </p>
      </div>

      <div className="pt-4">
        <Button type="submit" variant="primary" fullWidth disabled={submitting}>
          {submitting ? '제출 중...' : '문의하기'}
        </Button>
      </div>
    </form>
  );
};

export default InquiryForm;
